import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { LinkIcon } from "components/icons/LinkIcon";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Divider } from "components/design/Divider";
import { KeyDownWrapper } from "components/DesignSystem/KeydownWrapper/KeydownWrapper";
import { BankLogo } from "components/icons/BankLogo";
import { LongArrowRight } from "components/icons/LongArrowRight";
import { DISPLAY_DATE, YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { TRANSACTION_SOURCE } from "dictionaries";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useGetTransaction } from "hooks/useGetTransaction";
import { useModal } from "hooks/useModal";
import { useDispatch } from "react-redux";
import { openSlider } from "store/slices/transactions";
import { CategorySelector, MerchantSelectDelete } from "../TransactionColumn";
import { UnLinkTransaction } from "../UnLinkTransaction";
import { InfoItem } from "./TransactionSlider";
import { Transaction } from "types/Models/books";
import { Link } from "components/DesignSystem/Link/Link";
import { SplitCategoryOnPopover } from "../SplitCategoryOnPopover";
import { Button } from "components/DesignSystem/Button/Button";
import { BANK_CONNECTION_PROVIDER } from "constants/dataSource";
import { useState } from "react";
import { useGetExchangeRateQuery } from "store/apis/invoices";
import { Tick } from "components/icons/Tick";
import { FormikForm } from "components/FormikForm/FormikForm";
import { Refresh } from "components/icons/Refresh";
import { useToast } from "hooks/useToast";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useEditTransactionMutation } from "store/apis/transactions";
import { BackendError } from "types/utils/error";
import { CrossIcon } from "components/icons/CrossIcon";
import { PriceInput } from "components/PriceInput/PriceInput";
import { EditPencil } from "components/icons/EditPencil";
import { TickSlim } from "components/icons/TickSlim";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import classNames from "classnames";

export const Category = ({
  transactionId,
  isDisabled = false,
}: {
  transactionId: string;
  isDisabled?: boolean;
}) => {
  const { data: transactionDetail } = useGetTransaction({
    transactionId,
  });

  const { split_transactions = [], transaction } = transactionDetail || {};
  let tooltipText = transaction?.category?.name || "";

  if (split_transactions) {
    tooltipText = split_transactions.reduce(
      (ac, txn) => (ac += ac ? `, ${txn.category?.name}` : txn.category?.name),
      tooltipText
    );
  }

  if (transactionDetail) {
    if (split_transactions) {
      return (
        <InfoItem label="Category">
          <KeyDownWrapper>
            <SplitCategoryOnPopover
              transaction={transactionDetail}
              outline
              showSplitActions={!isDisabled}
              tooltipText={tooltipText}
              isDisabled={isDisabled}
            />
          </KeyDownWrapper>
        </InfoItem>
      );
    }
    return (
      <InfoItem label="Category">
        <KeyDownWrapper>
          <CategorySelector
            transaction={transactionDetail}
            outline
            showSplitActions={!isDisabled}
            txnsToShowInLabel={tooltipText}
            tooltip
            tooltipText={tooltipText}
            isDisabled={isDisabled}
          />
        </KeyDownWrapper>
      </InfoItem>
    );
  }

  return null;
};

const UsdAmountEditor = ({
  transactionId,
  usdAmount,
  amountBeforeEdit,
  originalAmount,
  exchangeRate,
  currency,
  date,
  disabled = false,
}: {
  transactionId: string;
  usdAmount: number;
  amountBeforeEdit?: number;
  originalAmount: number;
  exchangeRate?: number;
  currency: string;
  date: string;
  disabled?: boolean;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const { alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const formattedDate = date ? dayjs(date).format(YYYY_MM_DD) : "";

  const { data: rateData } = useGetExchangeRateQuery(
    { base_currency: "USD", target_currency: currency, date: formattedDate },
    { skip: !formattedDate || !currency }
  );

  const [editTransaction, { isLoading }] = useEditTransactionMutation();

  const rate = exchangeRate ?? rateData?.exchange_rate;

  const onSubmit = async ({ amount }: { amount: number }) => {
    try {
      await editTransaction({
        groupId,
        entityId,
        transactionId,
        amount: String(amount),
        exchange_rate: rate ? String(rate) : undefined,
      }).unwrap();
      setIsEditing(false);
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <div className="t-flex t-flex-col t-gap-1">
      <div className="t-flex t-items-center t-gap-2">
        <span className="t-text-body-sm">
          <AmountSuperScript amount={originalAmount} currencyCode={currency} />
        </span>
        <span className="t-text-text-60">~</span>
        {isEditing ? (
          <FormikForm
            initialValues={{ amount: usdAmount }}
            onSubmit={onSubmit}
            enableReinitialize
          >
            {({ submitForm, resetForm }) => (
              <div className="t-flex t-items-center t-gap-1">
                <div className="t-w-40">
                  <PriceInput
                    customSize="small"
                    name="amount"
                    placeholder="0.00"
                    rightComponent={
                      <Button
                        type="button"
                        customType="ghost_icon"
                        size="xsmall"
                        onClick={() =>
                          resetForm({
                            values: {
                              amount: amountBeforeEdit
                                ? amountBeforeEdit
                                : usdAmount,
                            },
                          })
                        }
                      >
                        <span style={{ transform: "scaleX(-1)" }}>
                          <Refresh size="16" stroke="1.5" />
                        </span>
                      </Button>
                    }
                  />
                </div>
                <Button
                  customType="icon"
                  size="small"
                  onClick={submitForm}
                  isLoading={isLoading}
                  type="button"
                >
                  <TickSlim />
                </Button>
                <Button
                  customType="icon"
                  size="small"
                  onClick={() => setIsEditing(false)}
                  type="button"
                >
                  <CrossIcon className="t-size-3" />
                </Button>
              </div>
            )}
          </FormikForm>
        ) : (
          <div className="t-flex t-items-center t-gap-1">
            <span className="t-text-body-italic t-italic t-text-text-100">
              <AmountSuperScript amount={usdAmount} />
            </span>
            {!disabled && (
              <Button
                customType="ghost_icon"
                size="xsmall"
                onClick={() => setIsEditing(true)}
                type="button"
              >
                <EditPencil color="currentColor" className="t-size-4" />
              </Button>
            )}
          </div>
        )}
      </div>

      <span className="t-text-body-sm-italic t-text-text-30 t-italic">
        As of the transaction date; 1 USD = {rate?.toFixed(2)} {currency}
      </span>
    </div>
  );
};

export const TransactionInfo = ({
  transactionId,
}: {
  transactionId: string;
}) => {
  const { link } = useConstructInternalLink();
  const { data: transactionDetail } = useGetTransaction({
    transactionId,
  });

  const dispatch = useDispatch();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.TRANSACTIONS
  );
  const {
    isOpen: isUnlinkOpen,
    close: closeUnlink,
    open: openUnlink,
  } = useModal();

  const {
    transaction: {
      date,
      amount,
      from,
      description,
      linked_transaction = "",
      ledger_entry,
      original_amount,
      exchange_rate,
      currency,
      amount_before_edit,
    } = {} as Transaction,
  } = transactionDetail || {};

  const isNonUSD = Boolean(currency && currency !== "USD");

  const isSplitTransaction = Array.isArray(
    transactionDetail?.split_transactions
  );
  const invoiceTransaction =
    transactionDetail?.has_linked_invoices || isSplitTransaction;
  const splitTransactionVendors = transactionDetail?.split_transactions
    ?.map((transaction) => transaction.merchant)
    .join(", ");

  const { entry_name, entry_number } = ledger_entry || {};

  const { stripe_aggregate_entry_ledger_entry } = transactionDetail || {};

  const isStripeTransaction =
    from?.bank_account?.bank_brand?.connection_provider ===
    BANK_CONNECTION_PROVIDER.STRIPE;

  return (
    <div className="t-rounded-lg t-border-neutral-10 t-border t-border-solid">
      <div className="t-p-4 t-flex t-gap-6 t-flex-col">
        <div className="t-flex t-justify-between t-gap-4">
          {Boolean(date) && (
            <InfoItem label="Date">
              <>{dayjs(date).format(DISPLAY_DATE)}</>
            </InfoItem>
          )}
        </div>

        <div className="">
          <InfoItem label="Amount">
            {isNonUSD ? (
              <UsdAmountEditor
                transactionId={transactionId}
                usdAmount={Number(amount)}
                amountBeforeEdit={amount_before_edit}
                originalAmount={Number(original_amount)}
                exchangeRate={exchange_rate}
                currency={currency!}
                date={date || ""}
                disabled={!hasWritePermission}
              />
            ) : (
              <AmountSuperScript amount={Number(amount)} />
            )}
          </InfoItem>
        </div>

        {transactionDetail?.transaction && (
          <InfoItem label="Vendor">
            <KeyDownWrapper>
              <MerchantSelectDelete
                outline
                transactions={transactionDetail}
                splitTransactionVendors={transactionDetail?.split_transactions}
                showSplitVendors={Boolean(splitTransactionVendors)}
                splitTransactionsMerchantCount={
                  transactionDetail?.split_transactions_merchant_count
                }
                showCategorisationHistoryAlways
                disabled={!hasWritePermission}
              />
            </KeyDownWrapper>
          </InfoItem>
        )}

        <InfoItem label="From">
          <div className="t-flex t-gap-1 t-items-center">
            {from?.bank_account?.bank_brand?.logo_url ? (
              <div>
                <Avatar
                  src={from?.bank_account?.bank_brand?.logo_url}
                  alt={from?.bank_account?.bank_brand?.name}
                />
              </div>
            ) : (
              <BankLogo />
            )}
            <div className="t-flex t-gap-1">
              {from?.bank_account && (
                <div>
                  {from?.bank_account?.nickname} ••••
                  {from?.bank_account?.mask}
                </div>
              )}
              {from?.source! === "LEDGER_ENTRY" && ledger_entry?.uuid ? (
                <Link
                  to={link(`/books/journal-entries`, {
                    moreQuery: {
                      journal_entry_id: ledger_entry?.uuid,
                    },
                  })}
                  target="_blank"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button customType="link" size="small">
                    {TRANSACTION_SOURCE[from?.source!]}
                  </Button>
                </Link>
              ) : (
                <div>({TRANSACTION_SOURCE[from?.source!]})</div>
              )}
            </div>
          </div>
        </InfoItem>
        {!invoiceTransaction && (
          <Category
            transactionId={transactionId}
            isDisabled={isStripeTransaction || !hasWritePermission}
          />
        )}

        {Boolean(description) && (
          <InfoItem label="Description">{description}</InfoItem>
        )}

        {Boolean(entry_name) && (
          <InfoItem label="Journal Entry Title">
            #{entry_number} {entry_name}
          </InfoItem>
        )}
        {Boolean(linked_transaction) && (
          <InfoItem
            label={
              <div className="t-flex t-flex-row t-justify-between">
                <div>Linked to</div>
                <div
                  className={classNames({
                    "t-pointer-events-none": !hasWritePermission,
                  })}
                >
                  <Button
                    customType="link"
                    size="small"
                    onClick={openUnlink}
                    className={classNames({
                      "!t-text-text-30": !hasWritePermission,
                    })}
                  >
                    Unlink
                  </Button>
                </div>
              </div>
            }
          >
            <Button
              customType="link"
              size="small"
              onClick={() => {
                dispatch(openSlider(linked_transaction));
              }}
            >
              Go to transaction
            </Button>
          </InfoItem>
        )}
        {stripe_aggregate_entry_ledger_entry && (
          <>
            <Divider color="dark-grey">
              <div className="t-flex t-items-center t-justify-center t-gap-1 t-text-body-sm t-text-text-60 t-w-40 t-bg-neutral-0 t-rounded-2xl t-p-1">
                <LinkIcon />
                Linked journal entry
              </div>
            </Divider>

            <div className="t-bg-neutral-66 t-p-4 t-rounded-lg t-border-neutral-10 t-border-solid t-border t-flex t-flex-col t-gap-3">
              <div className="t-text-subtext t-text-text-60">
                {stripe_aggregate_entry_ledger_entry.name}
              </div>
              <div className="t-text-body-sm t-text-text-30 t-flex t-items-center t-gap-2">
                {dayjs(
                  stripe_aggregate_entry_ledger_entry.aggregate_entry_date
                ).format(DISPLAY_DATE)}
              </div>
              <Link
                to={link(`/books/journal-entries`, {
                  moreQuery: {
                    journal_entry_id: ledger_entry?.uuid!,
                  },
                })}
                target="_blank"
                className="t-max-w-fit"
              >
                <Button customType="link" size="small">
                  View/Edit
                  <span className="t-ml-1">
                    <LongArrowRight className="t-h-3 t-w-3" />
                  </span>
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
      {isUnlinkOpen && (
        <UnLinkTransaction
          isOpen={isUnlinkOpen}
          close={closeUnlink}
          transactionDetail={transactionDetail}
        />
      )}
    </div>
  );
};
